import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY = 60;

/**
 * Undo/Redo via JSON snapshots of state.
 * Call saveState() BEFORE any mutation.
 * Provides undo(), redo(), canUndo, canRedo.
 */
export default function useUndoRedo(getState, setState) {
  const history = useRef([]);
  const future = useRef([]);
  const [version, setVersion] = useState(0); // force re-render on undo/redo

  const saveState = useCallback(() => {
    const snapshot = JSON.stringify(getState());
    history.current.push(snapshot);
    if (history.current.length > MAX_HISTORY) {
      history.current.shift();
    }
    future.current = [];
    setVersion(v => v + 1);
  }, [getState]);

  const undo = useCallback(() => {
    if (history.current.length === 0) return;
    const current = JSON.stringify(getState());
    future.current.push(current);
    const prev = history.current.pop();
    setState(JSON.parse(prev));
    setVersion(v => v + 1);
  }, [getState, setState]);

  const redo = useCallback(() => {
    if (future.current.length === 0) return;
    const current = JSON.stringify(getState());
    history.current.push(current);
    const next = future.current.pop();
    setState(JSON.parse(next));
    setVersion(v => v + 1);
  }, [getState, setState]);

  return {
    saveState,
    undo,
    redo,
    canUndo: history.current.length > 0,
    canRedo: future.current.length > 0,
    version,
  };
}
